// @flow
//import { FaceDetection } from '@mediapipe/face_detection';
//import { drawRectangle, drawLandmarks } from '@mediapipe/drawing_utils';
// Import background related functions or states
import { VIRTUAL_BACKGROUND_TYPE } from "../../virtual-background/constants";

//Import Face Recognition related libraires  and functions
import {
    FaceMesh,
    FACEMESH_TESSELATION,
    FACEMESH_RIGHT_EYE,
    FACEMESH_RIGHT_EYEBROW,
    FACEMESH_RIGHT_IRIS,
    FACEMESH_LEFT_EYE,
    FACEMESH_LEFT_EYEBROW,
    FACEMESH_LEFT_IRIS,
    FACEMESH_FACE_OVAL,
    FACEMESH_LIPS,
} from "@mediapipe/face_mesh";
import { drawConnectors } from "@mediapipe/drawing_utils";

//Import values related to both effects
import {
    CLEAR_TIMEOUT,
    SET_TIMEOUT,
    TIMEOUT_TICK,
    timerWorkerScript,
} from "./TimerWorker";
import { i18next } from "../../base/i18n";

import { getLocalVideoTrack } from "../../base/tracks";

//==============================================================================================================================================================
/**
 * Represents a modified MediaStream that adds effects to video background.
 * <tt>JitsiStreamBackgroundEffect</tt> does the processing of the original
 * video stream.
 */
export default class JitsiStreamBackgroundEffect {
    //Declaration of background model and option types which comes from index.js
    _backgroundModel: Object;
    _options: Object;

    //Declaration of background effect related types
    _segmentationPixelCount: number;
    _segmentationMaskCtx: Object;
    _segmentationMask: Object;
    _segmentationMaskCanvas: Object;
    _virtualImage: HTMLImageElement;
    _virtualVideo: HTMLVideoElement;

    //Declaration of face recognition related types
    _textifyFacialAttribute: Function;
    _onResults: Function;
    _tmpCanvasElement: HTMLCanvasElement;
    _tmpCanvasCtx: Object;
    _runFaceRecognition: Function;
    _faceMesh: FaceMesh;
    _facialAttribute: String;
    _avgAge: Number;
    _avgMan: Number;
    _avgWoman: Number;
    _fps: Number;
    _count: Number;

    // Declaration of common types for both effects
    _inputVideoElement: HTMLVideoElement;
    _outputCanvasElement: HTMLCanvasElement;
    _outputCanvasCtx: Object;
    _onMaskFrameTimer: Function;
    _masksFrameTimerWorker: Worker;
    _renderMasks: Function;
    isEnabled: Function;
    startEffect: Function;
    stopEffect: Function;
    APP: Object;

    //==============================================================================================================================================================

    /**
     * Represents a modified video MediaStream track.
     *
     * @class
     * @param {Object} model - Meet model.
     * @param {Object} options - Segmentation dimensions.
     */
    constructor(model: Object, options: Object) {
        //Initializing a new instance of the FaceMesh class and assigning it to the _faceMesh property of the current object
        this._faceMesh = new FaceMesh({
            locateFile: (file) => {
                return `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`;
            },
        });

        //Setting options for face mesh model
        this._faceMesh.setOptions({
            maxNumFaces: 1,
            minDetectionConfidence: 0.5,
            minTrackingConfidence: 0.5,
        });
        this._onResults = this._onResults.bind(this);

        //The _faceMesh object is assumed to be an instance of a face mesh model,
        // and the onResults method is used to register a callback function to be called when results are available.
        this._faceMesh.onResults(this._onResults);

        // These lines initialize several instance properties with default values.
        //_facialAttribute is set as an empty strin
        this._facialAttribute = "";
        this._avgAge = -1;
        this._avgMan = -1;
        this._avgWoman = -1;
        this._avgAsian = -1;
        this._avgBlack = -1;
        this._avgIndian = -1;
        this._avgLatinoHispanic = -1;
        this._avgMiddleEastern = -1;
        this._avgWhite = -1;
        this._fps = 30;
        this._count = 0;
        this.data = {};

        //These lines set the _options property to the provided options object
        this._options = options;
        if (this._options?.backgroundType === VIRTUAL_BACKGROUND_TYPE.IMAGE) {
            this._virtualImage = document.createElement("img");
            this._virtualImage.crossOrigin = "anonymous";
            this._virtualImage.src = this._options?.virtualSource;
        }

        // Make background modeling paramters into member variables
        this._backgroundModel = model;
        this._segmentationPixelCount =
            this._options.width * this._options.height;

        // Bind event handler so it is only bound once for every instance.
        this._onMaskFrameTimer = this._onMaskFrameTimer.bind(this);
        this._textifyFacialAttribute = this._textifyFacialAttribute.bind(this);
        this._onSendFaceImg = this._onSendFaceImg.bind(this);
        this._onCaptureFaceImg = this._onCaptureFaceImg.bind(this);

        // Workaround for FF issue https://bugzilla.mozilla.org/show_bug.cgi?id=1388974
        this._outputCanvasElement = document.createElement("canvas");
        this._outputCanvasElement.getContext("2d");
        this._inputVideoElement = document.createElement("video");

        this._tmpCanvasElement = document.createElement("canvas");
        this._tmpCanvasElement.getContext("2d");
    }

    //==============================================================================================================================================================
    // Key board shortcut implementation send face image to server
    _onSendFaceImg() {
        APP.keyboardshortcut.registerShortcut(
            "P",
            "sendFacetImg",
            this._onCaptureFaceImg,
            "keyboardShortcuts.sendFaceImg",
            true
        );
    }

    //==============================================================================================================================================================
    //Initializes an object named eng2kor with key-value pairs mapping English facial attribute names to Korean translations.

    _textifyFacialAttribute(data) {


        const emotion = {
            angry: i18next.t("faceRecognition.angry"),
            disgust: i18next.t("faceRecognition.disgust"),
            fear: i18next.t("faceRecognition.fear"),
            happy: i18next.t("faceRecognition.happy"),
            neutral: i18next.t("faceRecognition.neutral"),
            sad: i18next.t("faceRecognition.sad"),
            surprise: i18next.t("faceRecognition.surprise"),
        };

        const tracetranslationRace = {
            asian: i18next.t("faceRecognition.asian"),
            black: i18next.t("faceRecognition.black"),
            indian: i18next.t("faceRecognition.indian"),
            latinoHispanic: i18next.t("faceRecognition.latinoHispanic"),
            middleEastern: i18next.t("faceRecognition.middleEastern"),
            white: i18next.t("faceRecognition.white"),
        };

        // Update overall gender values and calculate gender age
        const ratio = 0.9;


        if (this._avgMan === -1 || this._avgWoman === -1) {
            this._avgMan = data.gender.man;
            this._avgWoman = data.gender.woman;
        } else {
            this._avgMan =
                ratio * this._avgMan + (1.0 - ratio) * data.gender.man;
            this._avgWoman =
                ratio * this._avgWoman + (1.0 - ratio) * data.gender.woman;
        }

        // Update average age value and calculate age group text
        let gender = i18next.t("faceRecognition.male");
        if (this._avgMan < this._avgWoman) {
            gender = i18next.t("faceRecognition.female");
        }

        if(this._avgAsian === -1 || this._avgIndian === -1 || this._avgWhite === -1 || this._avgBlack === -1 || this._avgLatinoHispanic === -1 || this._avgMiddleEastern === -1 ) {
            this._avgAsian = data.race.asian;
            this._avgIndian = data.race.indian;
            this._avgWhite = data.race.white;
            this._avgBlack = data.race.black;
            this._avgLatinoHispanic = data.race.latinoHispanic;
            this._avgMiddleEastern = data.race.middleEastern;
        } else{
            this._avgAsian =
                ratio * this._avgAsian + (1.0 - ratio) * data.race.asian;
            this._avgIndian =
                ratio * this._avgIndian + (1.0 - ratio) * data.race.indian;
            this._avgWhite =
                ratio * this._avgWhite + (1.0 - ratio) * data.race.white;
            this._avgBlack =
                ratio * this._avgBlack + (1.0 - ratio) * data.race.black;
            this._avgLatinoHispanic =
             ratio * this._avgLatinoHispanic + (1.0 - ratio) * data.race.latinoHispanic;
            this._avgMiddleEastern =
                ratio * this._avgMiddleEastern + (1.0 - ratio) * data.race.middleEastern;
        }

        let race = Math.max(this._avgAsian,this._avgIndian,this._avgBlack,this._avgWhite,this._avgLatinoHispanic,this._avgMiddleEastern);

        if(race === this._avgAsian){
            race = i18next.t("faceRecognition.asian")
        } else if( race === this._avgIndian){
            race = i18next.t("faceRecognition.indian")
        } else if ( race === this._avgWhite){
            race = i18next.t("faceRecognition.white")
        } else if ( race === this._avgBlack){
            race = i18next.t("faceRecognition.black")
        } else if ( race === this._avgLatinoHispanic){
            race = i18next.t("faceRecognition.latinoHispanic")
        } else {
            race = i18next.t("faceRecognition.middleEastern")
        }

        if (this._avgAge === -1) {
            this._avgAge = data.age;
        } else {
            this._avgAge = ratio * this._avgAge + (1.0 - ratio) * data.age;
        }

        const s = i18next.t("faceRecognition.s");
        let ageS;
        if (20 <= this._avgAge && this._avgAge < 30) {
            ageS = 20;
        }
        if (30 <= this._avgAge && this._avgAge < 40) {
            ageS = 30;
        }
        if (40 <= this._avgAge && this._avgAge < 50) {
            ageS = 40;
        }
        if (50 <= this._avgAge && this._avgAge < 60) {
            ageS = 50;
        }
        if (60 <= this._avgAge) {
            ageS = 60;
        }

        return `${ageS}${s} ${race} ${gender} ${emotion[data.dominantEmotion]}`;
    }
    //==============================================================================================================================================================
    /**
     * EventHandler onmessage for the maskFrameTimerWorker WebWorker.
     *
     * @private
     * @param {EventHandler} response - The onmessage EventHandler parameter.
     * @returns {void}
     */
    _onMaskFrameTimer(response: Object) {
        if (response.data.id === TIMEOUT_TICK) {
            this._renderMasks();
        }
    }
    //==============================================================================================================================================================
    /**
     * Represents the run post processing.
     *
     * @returns {void}
     */
    async runPostProcessing() {
        const track = this._stream.getVideoTracks()[0];
        const { height, width } = track.getSettings() ?? track.getConstraints();
        const { backgroundType } = this._options;

        this._outputCanvasElement.height = height;
        this._outputCanvasElement.width = width;

        this._outputCanvasCtx.globalCompositeOperation = "copy"; //Only the new shape is shown.
        this._outputCanvasCtx.drawImage(this._inputVideoElement, 0, 0);

        // //https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/globalCompositeOperation
        this._outputCanvasCtx.globalCompositeOperation = "copy"; //Only the new shape is shown.

        // // Draw segmentation mask.  =====> Detecting edges of human body...
        // // Smooth out the edges.

        this._outputCanvasCtx.filter =
            backgroundType === VIRTUAL_BACKGROUND_TYPE.IMAGE
                ? "blur(4px)"
                : "blur(8px)";

        // // draw segmetned people's mask...
        this._outputCanvasCtx.drawImage(
            this._segmentationMaskCanvas,
            0,
            0,
            this._options.width,
            this._options.height,
            0,
            0,
            this._inputVideoElement.width,
            this._inputVideoElement.height
        );

        this._outputCanvasCtx.globalCompositeOperation = "source-in";
        //The new shape is drawn only where both the new shape and the destination canvas overlap. Everything else is made transparent.

        this._outputCanvasCtx.filter = "none";

        // Draw the foreground video.  Area is drawed where mask is overlaped.
        this._outputCanvasCtx.drawImage(this._inputVideoElement, 0, 0);

        // Draw the background.
        this._outputCanvasCtx.globalCompositeOperation = "destination-over";

        //New shapes are drawn behind the existing canvas content.
        if (backgroundType === VIRTUAL_BACKGROUND_TYPE.IMAGE) {
            this._outputCanvasCtx.drawImage(
                backgroundType === VIRTUAL_BACKGROUND_TYPE.IMAGE
                    ? this._virtualImage
                    : this._virtualVideo,
                0,
                0,
                this._outputCanvasElement.width,
                this._outputCanvasElement.height
            );
        } else {
            this._outputCanvasCtx.filter = `blur(${this._options.blurValue}px)`;
            this._outputCanvasCtx.drawImage(this._inputVideoElement, 0, 0);
        }
    }
    //==============================================================================================================================================================

    // This method processes the results of facial recognition analysis and displays the output.
    _onResults(results) {
        // Retrieve the video track and its height and width settings
        const track = this._stream.getVideoTracks()[0];
        const { height, width } = track.getSettings() ?? track.getConstraints();

        // Get the current state from the store
        const state = APP.store.getState();
        const { localFlipX: currentFlipX } = state["features/base/settings"];

        // Set the dimensions of the output canvas
        this._outputCanvasElement.height = height;
        this._outputCanvasElement.width = width;

        // Draw the overlays on the output canvas
        this._outputCanvasCtx.save();
        this._outputCanvasCtx.clearRect(0, 0, width, height);
        this._outputCanvasCtx.drawImage(results.image, 0, 0, width, height);

        // Check if there are multiple face landmarks detected
        if (results.multiFaceLandmarks) {
            // Draw connectors between face landmarks to create a mask-like effect
            for (const landmarks of results.multiFaceLandmarks) {
                if (state["features/face-recognition"].maskEnabled) {
                    // Draw connectors for different facial features with specific colors
                    drawConnectors(
                        this._outputCanvasCtx,
                        landmarks,
                        FACEMESH_TESSELATION,
                        { color: "#C0C0C070", lineWidth: 1 }
                    );
                    drawConnectors(
                        this._outputCanvasCtx,
                        landmarks,
                        FACEMESH_RIGHT_EYE,
                        { color: "#FF3030" }
                    );
                    drawConnectors(
                        this._outputCanvasCtx,
                        landmarks,
                        FACEMESH_RIGHT_EYEBROW,
                        { color: "#FF3030" }
                    );
                    drawConnectors(
                        this._outputCanvasCtx,
                        landmarks,
                        FACEMESH_RIGHT_IRIS,
                        { color: "#FF3030" }
                    );
                    drawConnectors(
                        this._outputCanvasCtx,
                        landmarks,
                        FACEMESH_LEFT_EYE,
                        { color: "#30FF30" }
                    );
                    drawConnectors(
                        this._outputCanvasCtx,
                        landmarks,
                        FACEMESH_LEFT_EYEBROW,
                        { color: "#30FF30" }
                    );
                    drawConnectors(
                        this._outputCanvasCtx,
                        landmarks,
                        FACEMESH_LEFT_IRIS,
                        { color: "#30FF30" }
                    );
                    drawConnectors(
                        this._outputCanvasCtx,
                        landmarks,
                        FACEMESH_FACE_OVAL,
                        { color: "#E0E0E0" }
                    );
                    drawConnectors(
                        this._outputCanvasCtx,
                        landmarks,
                        FACEMESH_LIPS,
                        { color: "#E0E0E0" }
                    );
                }

                // Calculate the bounding box of the detected face
                let minX = 1,
                    minY = 1;
                let maxX = 0,
                    maxY = 0;
                for (let i = 0; i < landmarks.length; i++) {
                    // Update the minimum and maximum x and y coordinates
                    if (landmarks[i].x < minX) {
                        minX = landmarks[i].x;
                    }
                    if (landmarks[i].x > maxX) {
                        maxX = landmarks[i].x;
                    }
                    if (landmarks[i].y < minY) {
                        minY = landmarks[i].y;
                    }
                    if (landmarks[i].y > maxY) {
                        maxY = landmarks[i].y;
                    }
                }

                // Measure the width of some facial attribute text
                let backgroundWidth = this._outputCanvasCtx.measureText(
                    this._facialAttribute
                ).width;

                // Set the font, color, and alignment for drawing the facial attribute text
                this._outputCanvasCtx.font = "bolder 40px sans-serif";
                this._outputCanvasCtx.fillStyle = "#fff";
                this._outputCanvasCtx.direction = "ltr";
                this._outputCanvasCtx.scale(1, 1);
                this._outputCanvasCtx.strokeStyle = "#000";
                this._outputCanvasCtx.lineWidth = 2;

                let line = "";
                let fontSize = 40;
                let currentY = minY * height - 20;
                for(let i=0; i<this._facialAttribute.length; i++){
                    let tempLine = line + this._facialAttribute[i];
                    let tempWidth = this._outputCanvasCtx.measureText(tempLine).width;

                    if(this._facialAttribute[i] !== '\n') {
                        line = tempLine;
                    } else {
                        this._outputCanvasCtx.fillText(line, minX * width, currentY);
                        this._outputCanvasCtx.strokeText(line, minX * width, currentY);
                        if(this._facialAttribute[i] !== '\n') line = this._facialAttribute[i];
                        else line = "";
                        currentY += fontSize*1.2;
                    }
                }
                this._outputCanvasCtx.fillText(line, minX * width, currentY);
                this._outputCanvasCtx.strokeText(line, minX * width, currentY);

                // Draw the facial attribute text on the output canvas
                // this._outputCanvasCtx.fillText(
                //     this._facialAttribute,
                //     minX * width,
                //     minY * height - 20
                // );
                // this._outputCanvasCtx.strokeStyle = "#000";
                // this._outputCanvasCtx.lineWidth = 2;
                // this._outputCanvasCtx.strokeText(
                //     this._facialAttribute,
                //     minX * width,
                //     minY * height - 20
                // );
                this._outputCanvasCtx.textBaseline = "top";

                // this._drawTextBox(this._outputCanvasCtx, this._facialAttribute, minX*width, minY*height - 20, 1.2);

                this._outputCanvasCtx.restore();

                // Calculate the dimensions and position of the cropped image
                let cropWidth = (maxX - minX) * width * 1.2;
                let cropHeight = (maxY - minY) * height * 1.2;
                if (cropWidth < cropHeight) {
                    cropWidth = cropHeight;
                } else {
                    cropHeight = cropWidth;
                }
                let cx = ((maxX + minX) * width) / 2;
                let cy = ((maxY + minY) * height) / 2;
                let cropMinX = cx - cropWidth / 2;
                let cropMinY = cy - cropHeight / 2;
                this._tmpCanvasElement.width = cropWidth;
                this._tmpCanvasElement.height = cropHeight;
                this._tmpCanvasCtx.save();
                this._tmpCanvasCtx.drawImage(
                    results.image,
                    cropMinX,
                    cropMinY,
                    cropWidth,
                    cropHeight,
                    0,
                    0,
                    cropWidth,
                    cropHeight
                );

                // Convert the cropped image to a Data URL (JPEG format)

                let faceDetectImg =
                    this._tmpCanvasElement.toDataURL("image/jpeg");
                this._tmpCanvasCtx.restore();
                // Prepare the data to be sent to the server for further analysis
                const data = {
                    img: faceDetectImg,
                };

                // Send an AJAX request to the server for facial analysis
                // and update the facial attribute based on the response
                if (this._count !== 0) {
                    continue;
                }
                $.ajax({
                    url: "/livi/face/analyze",
                    type: "POST",
                    data: JSON.stringify(data),
                    context: this,
                    contentType: "application/json",
                    dataType: "JSON",
                    async: true,
                    success: function (data) {
                        this._facialAttribute =
                            this._textifyFacialAttribute(data);
                    },
                    error: function (res) {
                        console.log("face/analyze failed");
                    },
                });
                // this._onSendFaceImg();
            }
            // count 증가
            this._count = (this._count + 1) % (1 * this._fps*4);
        }

        // Restore the output canvas context
        this._outputCanvasCtx.restore();
    }

    //==============================================================================================================================================================

    //Made changes to  url api address in oder that other components could access to this function
    //Function to capture face image to send to server
    _onCaptureFaceImg(url) {
        let faceDetectImg = this._tmpCanvasElement.toDataURL("image/jpeg");
        let urldefault = "/livi/face/represent";
        urldefault = url;
        const data = {
            name: "신이지",
            img: faceDetectImg,
        };
        $.ajax({
            url: urldefault,
            type: "POST",
            data: JSON.stringify(data),
            context: this,
            contentType: "application/json",
            dataType: "JSON",
            async: true,
            success: function (data) {
                console.log(data);
            },
            error: function (res) {
                console.log("face/analyze failed");
            },
        });
    }

    //==============================================================================================================================================================
    /**
     * Represents the resize source process.
     *
     * @returns {void}
     */
    resizeSource() {
        this._segmentationMaskCtx.drawImage(
            this._inputVideoElement,
            0,
            0,
            this._inputVideoElement.width,
            this._inputVideoElement.height,
            0,
            0,
            this._options.width,
            this._options.height
        );

        const imageData = this._segmentationMaskCtx.getImageData(
            0,
            0,
            this._options.width,
            this._options.height
        );
        const inputMemoryOffset = this._backgroundModel._getInputMemoryOffset() / 4;

        for (let i = 0; i < this._segmentationPixelCount; i++) {
            this._backgroundModel.HEAPF32[inputMemoryOffset + i * 3] =
                imageData.data[i * 4] / 255;
            this._backgroundModel.HEAPF32[inputMemoryOffset + i * 3 + 1] =
                imageData.data[i * 4 + 1] / 255;
            this._backgroundModel.HEAPF32[inputMemoryOffset + i * 3 + 2] =
                imageData.data[i * 4 + 2] / 255;
        }
    }

    //==============================================================================================================================================================
    /*
     * Represents the run Tensorflow Interference.
     *
     * @returns {void}
     */
    runInference() {
        this._backgroundModel._runInference();
        const outputMemoryOffset = this._backgroundModel._getOutputMemoryOffset() / 4;

        for (let i = 0; i < this._segmentationPixelCount; i++) {
            const person = this._backgroundModel.HEAPF32[outputMemoryOffset + i];

            // Sets only the alpha component of each pixel.
            this._segmentationMask.data[i * 4 + 3] = 255 * person;
        }
        this._segmentationMaskCtx.putImageData(this._segmentationMask, 0, 0);
    }
    //==============================================================================================================================================================
    /*
     * Loop function to render Background effect and Face Mesh effect or none of them according to the enabled status of both effects
     *
     * @private
     * @returns {void}
     */
    async _renderMasks() {
        if (
            this._options.faceRecogEnabled === true &&
            (this._options?.backgroundEffectEnabled === undefined ||
                this._options?.backgroundEffectEnabled === false)
        ) {
            await this._faceMesh.send({ image: this._inputVideoElement });
        } else if (
            this._options.backgroundEffectEnabled === true &&
            this._options.faceRecogEnabled === false
        ) {
            this.resizeSource();
            this.runInference();
            this.runPostProcessing();
        } else if (
            this._options.backgroundEffectEnabled === true &&
            this._options.faceRecogEnabled === true
        ) {
            this.resizeSource();
            this.runInference();
            this.runPostProcessing();
            await this._faceMesh.send({ image: this._outputCanvasElement });
        } else if (this._options.faceRecogEnabled === false) {
            this._outputCanvasCtx.drawImage(this._inputVideoElement, 0, 0);
        }

        this._masksFrameTimerWorker.postMessage({
            id: SET_TIMEOUT,
            timeMs: 1000 / this._fps,
        });
    }

    //==============================================================================================================================================================
    /**
     * Checks if the local track supports this effect.
     *
     * @param {JitsiLocalTrack} jitsiLocalTrack - Track to apply effect.
     * @returns {boolean} - Returns true if this effect can run on the specified track
     * false otherwise.
     */
    isEnabled(jitsiLocalTrack: Object) {
        return (
            jitsiLocalTrack.isVideoTrack() &&
            jitsiLocalTrack.videoType === "camera"
        );
    }
    //==============================================================================================================================================================
    /**
     * Starts loop to capture video frame and render the Both Effects according to enabled status
     *
     * @param {MediaStream} stream - Stream to be used for processing.
     * @returns {MediaStream} - The stream with the applied effect.
     */
    startEffect(stream: MediaStream) {

        this._avgAge = -1;
        this._avgMan = -1;
        this._avgWoman = -1;
        this._count = 0;

        this._stream = stream;
        this._masksFrameTimerWorker = new Worker(timerWorkerScript, {
            name: "Blur effect worker",
        });
        this._masksFrameTimerWorker.onmessage = this._onMaskFrameTimer;
        const firstVideoTrack = this._stream.getVideoTracks()[0];
        const { height, frameRate, width } = firstVideoTrack.getSettings
            ? firstVideoTrack.getSettings()
            : firstVideoTrack.getConstraints();

      // Set with and heigh of segmentation mask only when Background effect is enabled
        if (this._options?.backgroundEffectEnabled === true) {
            this._segmentationMask = new ImageData(
                this._options.width,
                this._options.height
            );
        }

        this._outputCanvasElement.width = parseInt(width, 10);
        this._outputCanvasElement.height = parseInt(height, 10);
        this._outputCanvasCtx = this._outputCanvasElement.getContext("2d");

        this._tmpCanvasElement.width = parseInt(width, 10);
        this._tmpCanvasElement.height = parseInt(height, 10);
        this._tmpCanvasCtx = this._tmpCanvasElement.getContext("2d");

      // Create new canvas element for background mask only when Background effect is enabled
        if (this._options?.backgroundEffectEnabled === true) {
            this._segmentationMaskCanvas = document.createElement("canvas");
            this._segmentationMaskCanvas.width = this._options.width;
            this._segmentationMaskCanvas.height = this._options.height;
            this._segmentationMaskCtx =
                this._segmentationMaskCanvas.getContext("2d");
        }

        this._inputVideoElement.width = parseInt(width, 10);
        this._inputVideoElement.height = parseInt(height, 10);
        this._inputVideoElement.autoplay = true;
        this._inputVideoElement.srcObject = this._stream;
        this._inputVideoElement.onloadeddata = () => {
            this._masksFrameTimerWorker.postMessage({
                id: SET_TIMEOUT,
                timeMs: 1000 / 30,
            });
        };

        return this._outputCanvasElement.captureStream(parseInt(frameRate, 10));

    }
    //==============================================================================================================================================================
    /**
     * Stops the capture and render loop.
     *
     * @returns {void}
     */
    stopEffect() {
        this._masksFrameTimerWorker.postMessage({
            id: CLEAR_TIMEOUT,
        });
        this._masksFrameTimerWorker.terminate();
    }
    //==============================================================================================================================================================
}
